import React, { useMemo } from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { byStartTime, groupByWeekday, shortTime, todayWeekday } from "../../../utils/schedule";

/**
 * The tutor's recurring weekly timetable, grouped by weekday with today first.
 *
 * `tutor/timeslots` returns raw CourseTimeSlot models, so the weekday arrives as the nested
 * `week_days` relation rather than a string.
 *
 * Seat capacity is not rendered here: `course_time_slots.remaining_seats` is only ever written as a
 * copy of `seats`, so it would read as "all seats free" on a full class. Headcount lives on the
 * groups panel, where it is derived from real enrolments.
 */
export default function TutorTeachingWeek({ slots }) {
  const today = todayWeekday();

  const days = useMemo(() => {
    const grouped = groupByWeekday(slots, (s) => s.week_days?.name);
    grouped.forEach((d) => {
      d.rows = byStartTime(d.rows);
      d.isToday = d.key === today;
    });
    return [...grouped].sort((a, b) => Number(b.isToday) - Number(a.isToday));
  }, [slots, today]);

  if (days.length === 0) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
        <AccessTimeIcon sx={{ fontSize: 40, color: "rgba(0,0,0,0.26)" }} />
        <Typography variant="subtitle1" fontWeight={600}>
          No time slots yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your recurring class times will appear here once an admin adds them.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ maxHeight: 340, overflowY: "auto", pr: 0.5 }}>
      <Stack spacing={1.5}>
        {days.map((d) => (
          <Box key={d.key}>
            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
              <Typography
                variant="caption"
                sx={{
                  color: d.isToday ? "#7c3aed" : "#6b7280",
                  fontWeight: 800,
                  textTransform: "uppercase",
                }}
              >
                {d.name}
              </Typography>
              {d.isToday && (
                <Chip
                  size="small"
                  label="Today"
                  sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: "#ede9fe", color: "#7c3aed" }}
                />
              )}
            </Stack>
            <Stack spacing={0.75}>
              {d.rows.map((s) => (
                <Stack
                  key={s.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: d.isToday ? "#faf5ff" : "#f9fafb",
                    borderLeft: `3px solid ${d.isToday ? "#7c3aed" : "#e5e7eb"}`,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ color: "#111827", fontWeight: 700 }} noWrap>
                      {s.class_name || "Untitled slot"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }} noWrap display="block">
                      {[s.courses?.name, s.locations?.name].filter(Boolean).join(" · ") || "—"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#111827", fontWeight: 700, flexShrink: 0 }}
                  >
                    {shortTime(s.start_time)}–{shortTime(s.end_time)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
